import { Module } from 'vuex';
import axios from 'axios';

// PWA 사용자 타입
export type PWAUserType = 'manager' | 'boss' | 'employee' | null;

interface PWAUser {
  user_type: string;
  display_name: string;
  sisul_name: string;
  m_sisul_code?: string;
  // 센터장/관리자 필드
  m_idx?: number;
  m_id?: string;
  m_user_name?: string;
  m_sisul_name?: string;
  // 사원 필드
  emp_idx?: number;
  emp_id?: string;
  emp_code?: string;
  emp_name?: string;
  emp_birth?: string;
  emp_job?: string;
  emp_in_date?: string;
  emp_out_date?: string;
  emp_isboss?: string;
  emp_phone?: string;
  emp_email?: string;
  m_longterm_code?: string;
}

interface PWAState {
  selectedYear: number;
  selectedMonth: number;
  pwaUser: PWAUser | null;
  pwaUserType: PWAUserType;
  pwaLoginLoading: boolean;
  pwaAuthChecked: boolean;
}

// 초기화 시 localStorage에서 값 불러오기
const getInitialState = (): PWAState => {
  let year = new Date().getFullYear();
  let month = new Date().getMonth() + 1;
  
  if (typeof window !== 'undefined') {
    const savedYear = localStorage.getItem('pwa_selectedYear');
    const savedMonth = localStorage.getItem('pwa_selectedMonth');
    
    if (savedYear) {
      const parsedYear = parseInt(savedYear, 10);
      if (!isNaN(parsedYear)) {
        year = parsedYear;
      }
    }
    if (savedMonth) {
      const parsedMonth = parseInt(savedMonth, 10);
      if (!isNaN(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12) {
        month = parsedMonth;
      }
    }
  }
  
  return {
    selectedYear: year,
    selectedMonth: month,
    pwaUser: null,
    pwaUserType: null,
    pwaLoginLoading: false,
    pwaAuthChecked: false
  };
};

const pwaModule: Module<PWAState, any> = {
  namespaced: true,
  state: getInitialState,
  mutations: {
    setPwaYear(state, year: number) {
      state.selectedYear = year;
      if (typeof window !== 'undefined') {
        localStorage.setItem('pwa_selectedYear', year.toString());
      }
    },
    setPwaMonth(state, month: number) {
      state.selectedMonth = month;
      if (typeof window !== 'undefined') {
        localStorage.setItem('pwa_selectedMonth', month.toString());
      }
    },
    setPwaUser(state, { user, userType }: { user: PWAUser | null; userType: PWAUserType }) {
      state.pwaUser = user;
      state.pwaUserType = userType;
    },
    updatePwaUser(state, updates: Partial<PWAUser>) {
      if (state.pwaUser) {
        state.pwaUser = { ...state.pwaUser, ...updates };
      }
    },
    setPwaLoginLoading(state, loading: boolean) {
      state.pwaLoginLoading = loading;
    },
    setPwaAuthChecked(state, checked: boolean) {
      state.pwaAuthChecked = checked;
    },
    clearPwaUser(state) {
      state.pwaUser = null;
      state.pwaUserType = null;
      state.pwaAuthChecked = false; // 로그아웃 시 인증 체크 플래그도 리셋
    }
  },
  actions: {
    // PWA 전용 로그인 (hh_member + hh_emp 통합)
    async performPwaLogin({ commit, rootState }, { loginId, loginPw }) {
      commit('setPwaLoginLoading', true);
      try {
        const response = await axios.post('/api/auth/pwa/login', {
          loginId,
          loginPw
        }, {
          withCredentials: true,
          timeout: 10000
        });
        
        const result = response.data;
        
        if (result.success) {
          commit('setPwaUser', {
            user: result.user,
            userType: result.userType
          });
          
          // 메인 스토어의 user도 설정 (기존 시스템과 호환성 유지)
          if (result.userType === 'manager') {
            commit('setUser', result.user, { root: true });
          }
          
          commit('showSnackbar', { 
            text: `환영합니다!`, 
            color: 'success' 
          }, { root: true });
          
          return { 
            success: true, 
            userType: result.userType,
            user: result.user
          };
        } else {
          commit('showSnackbar', { 
            text: result.error || '로그인에 실패했습니다.', 
            color: 'error' 
          }, { root: true });
          return { success: false, error: result.error };
        }
      } catch (error: any) {
        console.error('PWA 로그인 실패:', error);
        const errorMessage = error.response?.data?.error || '로그인 중 오류가 발생했습니다.';
        commit('showSnackbar', { 
          text: errorMessage, 
          color: 'error' 
        }, { root: true });
        return { success: false, error: errorMessage };
      } finally {
        commit('setPwaLoginLoading', false);
      }
    },
    
    // PWA 로그인 상태 확인
    async validatePwaAuth({ commit }) {
      try {
        const response = await axios.get('/api/auth/pwa/account', {
          withCredentials: true
        });
        
        if (response.data.success) {
          commit('setPwaUser', {
            user: response.data.user,
            userType: response.data.userType
          });
          return { success: true, user: response.data.user, userType: response.data.userType };
        }
        return { success: false };
      } catch (error) {
        commit('clearPwaUser');
        return { success: false };
      }
    },
    
    // PWA 로그아웃
    async performPwaLogout({ commit }) {
      try {
        await axios.delete('/api/auth/account', {
          withCredentials: true
        });
      } catch (error) {
        console.error('PWA 로그아웃 오류:', error);
      } finally {
        commit('clearPwaUser');
        commit('clearState', null, { root: true });
      }
    }
  },
  getters: {
    pwaUser: (state) => state.pwaUser,
    pwaUserType: (state) => state.pwaUserType,
    isPwaLoggedIn: (state) => !!state.pwaUser,
    pwaLoginLoading: (state) => state.pwaLoginLoading,
    pwaAuthChecked: (state) => state.pwaAuthChecked,
    isManager: (state) => state.pwaUserType === 'manager',
    isBoss: (state) => state.pwaUserType === 'boss',
    isEmployee: (state) => state.pwaUserType === 'employee'
  }
};

export default pwaModule;


